<script>
    import Icon from '../icon/Icon.svelte';
    import Util from 'util/util';
    import Spinner from '../spinner/Spinner.svelte';
    import { fade, scale } from 'svelte/transition';

    export let title = ''
    export let isFullscreen = false
    export let isOpen = true
    export let hasCloseButton = true
    export let canBeClosedWithEscapeKey = false
    export let closeOnRoute = true

    // Override the modal's default max-width (in px), for content that needs more room
    export let maxWidth = null

    // Add margin to the inside of the modal, so that content aligns with the title
    export let hasMargin = true

    // Show a spinner until this request has loaded
    export let request = null

    // Set listener on layout view to close this modal when a route event happens
    if (closeOnRoute) {
        window.app.router.once('route', closeModalOnRoute)
    }

    let modalTriggerInput = null

    function containerCreated(element) {
        // Save the element which had focus when the modal got opened so we can put the focus
        // back on this element when the modal is closed.
        modalTriggerInput = document.activeElement

        setFocusTrap(element)

        window.statsTracker.trackEvent(
            window.app.controller.activePath.join('/'),
            'Modal opened: ' + title
        )

        return {
            destroy() {
                // Ensure route event listener is removed.
                window.app.router.off('route', closeModalOnRoute)

                // Restore focus on element which had focus when the element was opened. Check if
                // that element still exists and still can receive focus.
                if (modalTriggerInput?.matches(Util.focusableElementsSelector())) {
                    modalTriggerInput.focus()
                }
            }
        }
    }

    function setFocusTrap(element) {
        let firstFocusIndex

        // If there are buttons, focus on the last button
        if (element.querySelector('.buttons button')) {
            firstFocusIndex = -1

        // If there a no buttons, focus on the first element in the modal (skipping the close button)
        } else if (hasCloseButton) {
            firstFocusIndex = 1
        } else {
            firstFocusIndex = 0
        }

        Util.setFocusTrap(element, firstFocusIndex)
    }

    function onKeydown(e) {
        if (isOpen && canBeClosedWithEscapeKey && e.key === 'Escape') {
            isOpen = false
        }
    }

    function closeModalOnRoute() {
        isOpen = false
    }
</script>

<svelte:document on:keydown={onKeydown} />

{#if isOpen}
    <div
        data-disable-scrolling
        class="background"
        out:fade={{ duration: 350 }}
    >
        <div
            class="container"
            class:is-fullscreen={isFullscreen}
            style:max-width={maxWidth ? `${maxWidth}px` : null}
            style:width={maxWidth ? '100%' : null}
            role="dialog"
            aria-modal="true"
            use:containerCreated
            out:scale={{ duration: 350, start: 0.8 }}
        >

            <div class="title" class:has-close-button={hasCloseButton}>
                <slot name="title">
                    {title}
                </slot>
            </div>

            {#if hasCloseButton}
                <button
                    class="close"
                    on:click={() => { isOpen = false }}
                >
                    <Icon name="cross"></Icon>
                </button>
            {/if}

            <div class="content" class:has-margin={hasMargin}>
                {#await request}
                    <Spinner hasModalParent={true}></Spinner>
                {:then}
                    <slot></slot>
                {/await}
            </div>

            <div class="buttons">
                {#await request then}
                    <slot name="buttons"></slot>
                {/await}
            </div>

        </div>
    </div>
{/if}

<style lang="scss">/*
* Scut, a collection of Sass utilities
* to ease and improve our implementations of common style-code patterns.
* v1.0.1
* Docs at http://davidtheclark.github.io/scut
*/
/* TODO implement tooltip arrow */
.background {
  z-index: 12000;
  position: fixed;
  inset: 0;
  display: flex;
  background: rgba(0, 0, 0, 0.5);
  animation: backgroundIn 0.35s forwards;
}
@keyframes backgroundIn {
  0% {
    opacity: 0;
  }
}
.background .container {
  display: flex;
  flex-direction: column;
  min-width: min(100%, 560px);
  max-width: 820px;
  max-height: calc(100% - 32px);
  margin: auto;
  padding: 0;
  border-radius: 16px;
  border: none;
  background: #fff;
  animation: containerIn 0.35s forwards;
  overflow: hidden;
  mask-image: -webkit-radial-gradient(white, black);
}
@keyframes containerIn {
  0% {
    transform: scale(0.8);
    opacity: 0.4;
  }
}
.background .container.is-fullscreen {
  width: calc(100% - 32px);
  max-width: unset;
  height: calc(100% - 32px);
  max-height: unset;
  background: hsl(27, 15%, 93%);
}
@media (max-width: 620px) {
  .background .container.is-fullscreen {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }
}

.title {
  font-size: 20px;
  font-weight: 600;
  padding: 24px;
}
.title.has-close-button {
  padding-right: 64px;
}
.title:empty {
  display: none;
}

.close {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  margin: 24px 24px 24px 16px;
  cursor: pointer;
  transition: transform 0.1s ease-in;
}
.close:hover {
  transform: scale(1.2);
}
.close :global(svg) {
  width: 24px;
  height: 24px;
  fill: #122d40;
}

.content {
  display: flex;
  flex-direction: column;
  flex-shrink: 1;
  flex-grow: 1;
  overflow-y: auto;
}
.content.has-margin {
  padding: 0 24px 24px 24px;
}

.buttons {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 16px;
  border-top: 1px solid hsl(40, 4%, 84%);
}
.buttons:empty {
  display: none;
}</style>
